import { Helmet } from "@dr.pogodin/react-helmet";
import { useState } from "react";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

interface MileageEntry {
  id: string;
  date: string;
  destination: string;
  purpose: string;
  startOdometer: string;
  endOdometer: string;
  miles: number;
}

export default function MileageLog() {
  const IRS_RATE_2024 = 0.67; // 67 cents per mile for 2024
  const IRS_RATE_2025 = 0.70; // 70 cents per mile for 2025 (estimated)
  
  const [year, setYear] = useState<string>("2025");
  const [entries, setEntries] = useState<MileageEntry[]>([]);
  const [date, setDate] = useState("");
  const [destination, setDestination] = useState("");
  const [purpose, setPurpose] = useState("");
  const [startOdo, setStartOdo] = useState("");
  const [endOdo, setEndOdo] = useState("");

  const currentRate = year === "2024" ? IRS_RATE_2024 : IRS_RATE_2025;

  const addEntry = () => {
    if (!date || !destination || !purpose || !startOdo || !endOdo) {
      alert("Please fill in all fields");
      return;
    }

    const start = parseFloat(startOdo);
    const end = parseFloat(endOdo);
    
    if (isNaN(start) || isNaN(end) || end <= start) {
      alert("Invalid odometer readings");
      return;
    }

    const miles = parseFloat((end - start).toFixed(1));
    
    const newEntry: MileageEntry = {
      id: Date.now().toString(),
      date,
      destination,
      purpose,
      startOdometer: startOdo,
      endOdometer: endOdo,
      miles
    };

    setEntries([...entries, newEntry]);
    
    // Clear form
    setDate("");
    setDestination("");
    setPurpose("");
    setStartOdo("");
    setEndOdo("");
  };

  const deleteEntry = (id: string) => {
    setEntries(entries.filter(e => e.id !== id));
  };

  const totalMiles = entries.reduce((sum, e) => sum + e.miles, 0);
  const totalDeduction = totalMiles * currentRate;

  const downloadCSV = () => {
    const headers = ["Date", "Destination", "Purpose", "Start Odometer", "End Odometer", "Miles", "Deduction"];
    const rows = entries.map(e => [
      e.date,
      e.destination,
      e.purpose,
      e.startOdometer,
      e.endOdometer,
      e.miles.toFixed(1),
      `$${(e.miles * currentRate).toFixed(2)}`
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mileage-log-${year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printLog = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Free Mileage Log Template & Calculator | IRS-Compliant Tracker</title>
        <meta
          name="description"
          content="Free mileage log template and calculator for 1099 contractors. Track business miles, calculate deductions at IRS rates, and download your log for tax time."
        />
        <meta name="keywords" content="mileage log, irs mileage tracker, business miles calculator, mileage deduction, vehicle expense tracker" />
        <link rel="canonical" href="https://free1099calc.com/tools/mileage-log" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full mb-6">
              <span className="text-blue-300 text-sm font-semibold">🚗 Mileage Tracker</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Mileage Log & Calculator
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Track business miles and calculate your IRS-compliant mileage deduction at 70¢/mile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#tracker"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Start Tracking →
              </a>
              <Link
                to="/tools"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                More Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12">

        {/* Expense Tracker Banner */}
        <div className="mb-8">
          <ExpenseTrackerBanner compact />
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border-l-4 border-primary-600 p-4 mb-8">
          <h3 className="font-semibold text-slate-900 mb-2">IRS Standard Mileage Rates</h3>
          <div className="text-sm text-slate-700 space-y-1">
            <p>• <strong>2025:</strong> 70¢ per mile (estimated)</p>
            <p>• <strong>2024:</strong> 67¢ per mile</p>
            <p>• <strong>2023:</strong> 65.5¢ per mile (reference)</p>
          </div>
          <p className="text-xs text-slate-600 mt-2">
            This calculator uses the standard mileage rate method. You cannot use this if you've already claimed
            actual vehicle expenses using depreciation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Add Mileage Entry</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax Year</label>
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="2025">2025 (70¢/mile)</option>
                    <option value="2024">2024 (67¢/mile)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Destination</label>
                <input
                  type="text"
                  placeholder="e.g., Client meeting at 123 Main St"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Purpose</label>
                <input
                  type="text"
                  placeholder="e.g., Project consultation, supply pickup"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Odometer</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="12345.6"
                    value={startOdo}
                    onChange={(e) => setStartOdo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Odometer</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="12370.3"
                    value={endOdo}
                    onChange={(e) => setEndOdo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <button
                onClick={addEntry}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Add Entry
              </button>
            </div>

            {/* Entries table */}
            {entries.length > 0 && (
              <div className="bg-white rounded-lg border shadow-sm p-6 print:shadow-none">
                <div className="flex items-center justify-between mb-4 print:mb-6">
                  <h2 className="text-xl font-bold text-slate-900">Mileage Log for {year}</h2>
                  <div className="flex gap-2 print:hidden">
                    <button
                      onClick={downloadCSV}
                      className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                    >
                      Download CSV
                    </button>
                    <button
                      onClick={printLog}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      Print Log
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b-2 border-slate-300">
                      <tr className="text-left">
                        <th className="pb-2 font-semibold">Date</th>
                        <th className="pb-2 font-semibold">Destination</th>
                        <th className="pb-2 font-semibold">Purpose</th>
                        <th className="pb-2 font-semibold text-right">Miles</th>
                        <th className="pb-2 font-semibold text-right">Deduction</th>
                        <th className="pb-2 font-semibold print:hidden"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-200">
                          <td className="py-3">{entry.date}</td>
                          <td className="py-3">{entry.destination}</td>
                          <td className="py-3">{entry.purpose}</td>
                          <td className="py-3 text-right">{entry.miles.toFixed(1)}</td>
                          <td className="py-3 text-right font-medium">${(entry.miles * currentRate).toFixed(2)}</td>
                          <td className="py-3 text-right print:hidden">
                            <button
                              onClick={() => deleteEntry(entry.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Summary sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border shadow-sm p-6 sticky top-20">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Deduction Summary</h2>
              
              <div className="space-y-4">
                <div className="pb-4 border-b">
                  <div className="text-sm text-slate-600 mb-1">Total Business Miles</div>
                  <div className="text-3xl font-bold text-slate-900">{totalMiles.toFixed(1)}</div>
                </div>
                
                <div className="pb-4 border-b">
                  <div className="text-sm text-slate-600 mb-1">IRS Rate ({year})</div>
                  <div className="text-2xl font-bold text-slate-900">${currentRate.toFixed(2)}/mi</div>
                </div>

                <div className="pt-2">
                  <div className="text-sm text-slate-600 mb-1">Total Deduction</div>
                  <div className="text-3xl font-bold text-primary-600">${totalDeduction.toFixed(2)}</div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-slate-700">
                    <strong>Remember:</strong> Only business miles are deductible. Commuting to your regular workplace
                    is not deductible, but travel between job sites is.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Educational content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">What Qualifies as Business Mileage?</h2>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Driving to meet clients or customers</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Traveling between multiple work locations</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Running business errands (bank, post office, supplies)</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Driving to temporary work locations</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Attending business meetings, conferences, or training</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 mr-2">✗</span>
                <span><strong>NOT deductible:</strong> Commuting from home to your regular workplace</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 mr-2">✗</span>
                <span><strong>NOT deductible:</strong> Personal errands or trips</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">IRS Documentation Requirements</h2>
            <p className="text-sm text-slate-700 mb-4">
              To claim the mileage deduction, the IRS requires you to maintain a log with:
            </p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span><strong>Date</strong> of each trip</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span><strong>Starting point</strong> and <strong>destination</strong></span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span><strong>Business purpose</strong> for the trip</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">•</span>
                <span><strong>Miles driven</strong> (odometer readings)</span>
              </li>
            </ul>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-slate-700">
                <strong>Pro tip:</strong> Use a mileage tracking app or this tool monthly. Reconstructing logs from
                memory during tax season is difficult and may not satisfy IRS requirements.
              </p>
            </div>
          </div>
        </div>

        {/* Related resources */}
        <div className="mt-12">
          <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Calculate Your Total Tax Savings</h2>
            <p className="text-slate-600 mb-6">
              Include your mileage deduction and other business expenses in our comprehensive tax calculator
            </p>
            <Link
              to="/"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Calculate My Taxes →
            </Link>
          </div>
        </div>

        {/* Recent Posts */}
        <div className="mt-12 print:hidden">
          <RecentPosts />
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:mb-6 { margin-bottom: 1.5rem !important; }
          body { background: white; }
          .bg-slate-50 { background: white; }
        }
      `}</style>
    </div>
    </>
  );
}
